<template>
  <mds-modal visible action-required aria-label="Default Modal">
    <mds-section
      bold
      :size="5"
      border="primary"
      border-position="bottom"
      :title="translate('selectBenefitsAndRiders')"
    >
      <template v-slot:mds-section-actions>
        <mds-button-container>
          <mds-button variation="secondary" type="button" @click="cancelDialog">{{
            translate('cancel')
          }}</mds-button>
          <mds-button variation="primary" type="button" @click="saveDialog">{{
            translate('close')
          }}</mds-button>
        </mds-button-container>
      </template>
      <template>
        <div :class="$style['content']">
          <mds-layout-grid>
            <mds-row>
              <mds-col :cols="3">
                <strong>{{ translate('contract') }}</strong>
              </mds-col>
              <mds-col>
                <strong> {{ translate('totalOptionalBenefitCharges') }} </strong>
              </mds-col>
            </mds-row>
            <mds-row :class="$style['contract-subheading']">
              <mds-col :cols="3">{{ policyInfo.policyName }}</mds-col>
              <mds-col v-html="totalFee"></mds-col>
            </mds-row>
          </mds-layout-grid>
          <mds-section border="tertiary" :class="$style['tertiary-section']">
            <template>
              <mds-switch
                v-model="hideClosedBenefits"
                :label="translate('hideClosedBenefits')"
              ></mds-switch>
            </template>
          </mds-section>
          <mds-table multiselection :class="$style['annuity-table']">
            <mds-thead>
              <mds-th v-for="item in headers" :key="item.fieldName" :style="item.style">
                {{ translate(item.labelKey) }}
              </mds-th>
            </mds-thead>
            <mds-tbody>
              <template v-for="(rider, riderId) in riders">
                <!-- rider without multiple options -->
                <mds-tr
                  v-if="rider.expanded === undefined && includeClosedBenefits(rider)"
                  :key="riderId"
                  :checked="rider.selectedOption !== null"
                  @mds-tr-multiselection-changed="handleSelect(riderId, $event)"
                >
                  <mds-td v-for="item in headers" :key="item.fieldName">
                    <template
                      v-if="
                        item.fieldName === 'closeDate' && rider.option1[item.fieldName] === 'Open'
                      "
                    >
                      {{ translate('benefitOpen') }}
                    </template>
                    <span v-else v-html="rider.option1[item.fieldName]"></span>
                  </mds-td>
                </mds-tr>
                <!-- rider with multiple options -->
                <mds-tr
                  v-if="showRowWithMultipleOptions(rider)"
                  :key="riderId"
                  type="expandable"
                  :checked="rider.selectedOption !== null"
                  :expanded="rider.expanded"
                  @click.native="handleExpand(riderId)"
                  @keyup.enter.native="handleExpand(riderId)"
                  @mds-tr-multiselection-changed="handleSelect(riderId, $event)"
                >
                  <mds-td v-for="item in headers.slice(0, 4)" :key="item.fieldName">
                    <template
                      v-if="
                        item.fieldName === 'closeDate' && rider.option1[item.fieldName] === 'Open'
                      "
                    >
                      {{ translate('benefitOpen') }}
                    </template>
                    <span v-else v-html="rider.option1[item.fieldName]"></span>
                  </mds-td>
                  <mds-td>
                    {{ rider.selectedFee !== null ? rider.selectedFee : '&mdash;' }}
                  </mds-td>
                </mds-tr>
                <!-- expanded area -->
                <template v-for="(option, optionKey) in rider">
                  <mds-tr
                    v-if="
                      showRowWithMultipleOptions(rider) &&
                        rider.expanded &&
                        optionKey.includes('option')
                    "
                    :key="riderId + option.riderOptionName"
                    :class="$style['expanded-row']"
                    type="container"
                  >
                    <!-- use div to occpy the first cell checkbox space -->
                    <div></div>
                    <mds-td colspan="1">
                      <mds-form>
                        <mds-radio-button
                          :class="$style['first-cell']"
                          name="rider-option"
                          :value="option.riderOptionName"
                          :checked="rider.selectedOption === option.riderOptionName"
                          @input="handleRadio(riderId, option.riderOptionName, option.fee)"
                        >
                          {{ option.riderOptionName }}
                        </mds-radio-button>
                      </mds-form>
                    </mds-td>
                    <mds-td v-for="i in 3" :key="i" colspan="1" v-html="'&mdash;'"></mds-td>
                    <mds-td colspan="1">{{ option.fee }} </mds-td>
                  </mds-tr>
                </template>
              </template>
            </mds-tbody>
          </mds-table>
          <annuity-dialog-custom-input
            :mwc-id="getMwcIdForChild('living-benefit-rider')"
            :title="translate('livingBenefitRider')"
            :saved-data="customRiders.livingBenefitRider"
            @input-changed="customRiders.livingBenefitRider = $event"
          >
          </annuity-dialog-custom-input>
          <annuity-dialog-custom-input
            :mwc-id="getMwcIdForChild('death-benefit-rider')"
            :title="translate('deathBenefitRider')"
            :saved-data="customRiders.deathBenefitRider"
            @input-changed="customRiders.deathBenefitRider = $event"
          >
          </annuity-dialog-custom-input>
          <annuity-dialog-custom-input
            :mwc-id="getMwcIdForChild('credit-benefit-rider')"
            :with-credit="true"
            :title="translate('creditBenefitRider')"
            :saved-data="customRiders.creditBenefitRider"
            @input-changed="customRiders.creditBenefitRider = $event"
          >
          </annuity-dialog-custom-input>
        </div>
      </template>
    </mds-section>
  </mds-modal>
</template>

<script>
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import MdsSection from '@mds/section';
import MdsModal from '@mds/modal';
import { MdsTable, MdsThead, MdsTh, MdsTbody, MdsTr, MdsTd } from '@mds/data-table';
import { MdsButton, MdsButtonContainer } from '@mds/button';
import { MdsLayoutGrid, MdsRow, MdsCol } from '@mds/layout-grid';
import MdsSwitch from '@mds/switch';
import MdsForm from '@mds/form';
import MdsRadioButton from '@mds/radio-button';
import cloneDeep from 'lodash.clonedeep';
import { getApiBaseUrl } from 'hypo-shared-utils';
import { isObjEmpty, isNumber } from '../../utils/utils';
import AnnuityDialogCustomInput from './AnnuityDialogCustomInput';

export default {
  name: 'annuity-dialog',
  components: {
    MdsSection,
    MdsTable,
    MdsThead,
    MdsTh,
    MdsTbody,
    MdsTr,
    MdsTd,
    MdsButton,
    MdsButtonContainer,
    MdsModal,
    MdsLayoutGrid,
    MdsRow,
    MdsCol,
    MdsSwitch,
    MdsForm,
    MdsRadioButton,
    AnnuityDialogCustomInput,
  },
  mixins: [MwcVueHelperMixin, MwcConfigMixin],
  props: {
    policyInfo: {
      type: Object,
      default: () => {},
    },
    dateFormat: {
      type: String,
      default: 'MM/DD/YYYY',
    },
  },
  data() {
    return {
      hideClosedBenefits: false,
      customRiders: {
        livingBenefitRider: {
          include: false,
          name: null,
          fee: 0,
          description: null,
        },
        deathBenefitRider: {
          include: false,
          name: null,
          fee: 0,
          description: null,
        },
        creditBenefitRider: {
          include: false,
          name: null,
          fee: 0,
          credit: 0,
          description: null,
        },
      },
      riders: {},
    };
  },
  computed: {
    totalFee() {
      let result = 0;
      let anySelected = false;
      Object.keys(this.riders).forEach(riderId => {
        const rider = this.riders[riderId];
        if (rider.selectedOption !== null && rider.selectedFee !== null) {
          result += rider.selectedFee;
          anySelected = true;
        }
      });
      Object.keys(this.customRiders).forEach(key => {
        const customRider = this.customRiders[key];
        if (customRider.include) {
          result += customRider.fee;
          anySelected = true;
        }
      });
      return anySelected ? this.formatter.format(result, 'shortDecimal') : '&mdash;';
    },
    headers() {
      return [
        {
          labelKey: 'benefit',
          fieldName: 'riderName',
          style: {
            width: '35%',
          },
        },
        {
          labelKey: 'benefitType',
          fieldName: 'riderType',
          style: {
            width: '25%',
          },
        },
        {
          labelKey: 'benefitOpenDate',
          fieldName: 'openDate',
          style: {
            width: '15%',
          },
        },
        {
          labelKey: 'benefitCloseDate',
          fieldName: 'closeDate',
          style: {
            width: '15%',
          },
        },
        {
          labelKey: 'benefitFee',
          fieldName: 'fee',
          style: {
            width: '10%',
          },
        },
      ];
    },
  },
  watch: {
    hideClosedBenefits: {
      handler(val) {
        if (val) {
          // deselect closed benefits riders
          Object.keys(this.riders).forEach(riderId => {
            const rider = this.riders[riderId];
            if (rider.isClosedBenefit) {
              this.$set(rider, 'selectedOption', null);
              this.$set(rider, 'selectedFee', null);
            }
            if (rider.expanded) {
              this.$set(rider, 'expanded', false);
            }
          });
        }
      },
    },
  },
  created() {
    this.getRiders();
  },
  methods: {
    getRiders() {
      if (!this.policyInfo) {
        return;
      }

      window.mwc.dataAccess
        .fetch({
          source: this.settings.tokenSource,
          url: `${getApiBaseUrl('/api/v1/fee/getriders', this.options.environment)}`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(this.policyInfo.policyId),
        })
        .then(res => res.json())
        .then(data => {
          // process data, change to object structure
          // add "selectedOption, selectedFee, isClosedBenefit" properties
          // format date, apply open/close date logics
          data.forEach(item => {
            const { isClosedBenefit, parsedItem } = this.parseDate(item);
            const rider = this.riders[item.riderId];
            if (!rider) {
              this.$set(this.riders, item.riderId, {
                selectedOption: null,
                selectedFee: null,
                isClosedBenefit,
                option1: parsedItem,
              });
            } else {
              rider.expanded = false;
              // 3 properties (selectedOption, selectedFee, isClosedBenefit) should not be counted as options
              rider[`option${Object.keys(rider).length - 3}`] = parsedItem;
            }
          });
          // apply saved selected/custom riders setting
          this.processRidersAccordingToSettings();
        });
    },
    processRidersAccordingToSettings() {
      if (this.policyInfo && this.policyInfo.policySetting) {
        const { selectedRiders, customRiders, hideClosedBenefits } = this.policyInfo.policySetting;
        this.hideClosedBenefits = hideClosedBenefits || false;
        if (selectedRiders) {
          Object.keys(selectedRiders).forEach(riderId => {
            const rider = this.riders[riderId];
            rider.selectedOption = selectedRiders[riderId].option;
            rider.selectedFee = selectedRiders[riderId].fee;
            if (rider.expanded !== undefined) {
              rider.expanded = true;
            }
          });
        }
        if (customRiders) {
          Object.keys(customRiders).forEach(key => {
            this.customRiders[key] = { ...this.customRiders[key], ...customRiders[key] };
          });
        }
      }
    },
    cancelDialog() {
      this.$emit('dialog-cancelled');
    },
    saveDialog() {
      const selectedRiders = {};
      Object.keys(this.riders).forEach(riderId => {
        const rider = this.riders[riderId];
        if (rider.selectedOption !== null) {
          selectedRiders[riderId] = { option: rider.selectedOption, fee: rider.selectedFee };
        }
      });

      const customRiders = {};
      Object.keys(this.customRiders).forEach(customKey => {
        const customRider = this.customRiders[customKey];
        if (customRider.include) {
          customRiders[customKey] = { ...customRider };
        }
      });

      const policySetting = {
        hideClosedBenefits: this.hideClosedBenefits,
      };
      if (!isObjEmpty(selectedRiders)) {
        policySetting.selectedRiders = selectedRiders;
      }
      if (!isObjEmpty(customRiders)) {
        policySetting.customRiders = customRiders;
      }
      // if no rider is selected, totalFee = '&mdash;'
      if (isNumber(this.totalFee)) {
        policySetting.totalFee = Number(this.totalFee);
      }
      this.$emit('dialog-saved', policySetting);
    },
    handleExpand(riderId) {
      this.$set(this.riders[riderId], 'expanded', !this.riders[riderId].expanded);
      this.riders = { ...this.riders }; // force rerender
    },
    handleSelect(riderId, isChecked) {
      const rider = this.riders[riderId];
      if (rider.expanded !== undefined) {
        this.$set(rider, 'expanded', isChecked);
      }
      if (isChecked) {
        if (rider.selectedOption === null) {
          rider.selectedOption = rider.option1.riderOptionName;
          rider.selectedFee = rider.option1.fee;
        }
      } else {
        this.$set(rider, 'selectedOption', null);
        this.$set(rider, 'selectedFee', null);
      }
    },
    handleRadio(riderId, optionName, fee) {
      this.$set(this.riders[riderId], 'selectedOption', optionName);
      this.$set(this.riders[riderId], 'selectedFee', fee);
    },
    parseDate(rider) {
      const formatDate = date => {
        const dateDict = {
          dd: date.getDate(),
          mm: date.getMonth() + 1,
          yyyy: date.getFullYear(),
        };
        return this.dateFormat
          .split('/')
          .map(dateKey => {
            return dateDict[dateKey.toLowerCase()];
          })
          .join('-');
      };

      let isClosedBenefit = true;
      const open = new Date(rider.openDate);
      const close = new Date(rider.closeDate);
      const clonedRider = cloneDeep(rider);
      clonedRider.openDate = formatDate(open);
      clonedRider.closeDate = formatDate(close);

      if (open.getTime() === close.getTime() || !rider.closeDate || rider.closeDate === 'Open') {
        // not closedBenefit, close show open
        isClosedBenefit = false;
        clonedRider.closeDate = 'Open';
      } else if (open > close) {
        // is closedBenefit, error data, dash openDate
        clonedRider.openDate = '&mdash;';
      } else {
        // is closedBenefit, show normally
      }
      return { isClosedBenefit, parsedItem: clonedRider };
    },
    includeClosedBenefits(rider) {
      return !(rider.isClosedBenefit && this.hideClosedBenefits);
    },
    showRowWithMultipleOptions(rider) {
      return rider.expanded !== undefined && this.includeClosedBenefits(rider);
    },
  },
};
</script>

<style lang="scss" module>
@import '@mds/typography';

.content {
  max-height: 75vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 $mds-space-three-quarter-x;
  padding-bottom: $mds-space-6-x;
}

.contract-subheading.contract-subheading {
  margin-top: $mds-space-1-x;
  margin-bottom: $mds-space-1-x;
}

.tertiary-section {
  margin-bottom: $mds-space-5-x;
  .mds-section__header-container {
    margin-top: $mds-space-2-x;
    margin-bottom: $mds-space-three-quarter-x;
  }
  .mds-switch {
    max-width: 200px;
  }
}

.annuity-table {
  margin-bottom: $mds-space-3-and-a-half-x;
  thead th .mds-checkbox {
    display: none;
  }
  .expanded-row {
    .first-cell {
      margin-left: $mds-space-3-and-a-half-x;
    }
  }
}
</style>
